import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Modal, Form, Input, InputNumber, Switch, Upload, Space, Typography, Popconfirm, Select } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { consultantApi, storeApi, uploadApi } from '../../services/api';

const { Title } = Typography;

const AvatarUploader = ({ value = {}, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const customRequest = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await uploadApi.uploadImage(formData);
      onChange({ url: res.data.url });
      message.success('照片上传成功');
      onSuccess(res);
    } catch (err) {
      message.error(err.message || '照片上传失败');
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Upload accept="image/jpeg,image/png,image/webp" listType="picture-card" showUploadList={false} customRequest={customRequest}>
      {value?.url ? (
        <img src={value.url} alt="顾问照片" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <div>
          <UploadOutlined />
          <div style={{ marginTop: 8 }}>{uploading ? '上传中...' : '上传照片'}</div>
        </div>
      )}
    </Upload>
  );
};

const Consultants = () => {
  const [data, setData] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [res, storesRes] = await Promise.all([consultantApi.list(), storeApi.list({ status: 1, limit: 100 })]);
      setData(res.data || []);
      setStores(storesRes.data?.list || storesRes.data || []);
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: true, sortOrder: 0 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone,
      wechat: record.wechat,
      title: record.title,
      avatar: record.avatar_url ? { url: record.avatar_url } : {},
      storeId: record.store_id,
      sortOrder: record.sort_order,
      status: record.status === 1,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        phone: values.phone || undefined,
        wechat: values.wechat || undefined,
        title: values.title || undefined,
        avatarUrl: values.avatar?.url || undefined,
        storeId: values.storeId || undefined,
        sortOrder: values.sortOrder,
        status: values.status ? 1 : 0,
      };
      if (editing) {
        await consultantApi.update(editing.id, payload);
      } else {
        await consultantApi.create(payload);
      }
      message.success(editing ? '更新成功' : '创建成功');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      message.error(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await consultantApi.remove(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '照片', dataIndex: 'avatar_url', width: 70,
      render: (v) => v ? <img src={v} alt="" style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }} /> : '-'
    },
    { title: '姓名', dataIndex: 'name', width: 100 },
    { title: '头衔', dataIndex: 'title', width: 130, render: (v) => v || '-' },
    { title: '电话', dataIndex: 'phone', width: 130 },
    { title: '微信号', dataIndex: 'wechat', width: 120, render: (v) => v || '-' },
    { title: '所属门店', dataIndex: 'store_name', width: 130, render: (v) => v || '-' },
    { title: '排序', dataIndex: 'sort_order', width: 70 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v) => v === 1 ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>
    },
    {
      title: '操作', width: 140, render: (_, r) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该顾问？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>顾问管理</Title>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: '#8B7355' }} onClick={openCreate}>
          新增顾问
        </Button>
      </div>

      <Table columns={columns} dataSource={data} rowKey="id" loading={loading}
        pagination={{ showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }} />

      <Modal
        title={editing ? '编辑顾问' : '新增顾问'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { background: '#8B7355' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }, { max: 50 }]}>
            <Input placeholder="顾问姓名" maxLength={50} />
          </Form.Item>
          <Form.Item name="avatar" label="照片">
            <AvatarUploader />
          </Form.Item>
          <Form.Item name="title" label="头衔" rules={[{ max: 50 }]}>
            <Input placeholder="如 高级珠宝顾问" maxLength={50} />
          </Form.Item>
          <Form.Item name="phone" label="联系电话" rules={[{ max: 20 }]}>
            <Input placeholder="用于小程序一键拨打" maxLength={20} />
          </Form.Item>
          <Form.Item name="wechat" label="微信号" rules={[{ max: 64 }]}>
            <Input placeholder="用于小程序微信联系" maxLength={64} />
          </Form.Item>
          <Form.Item name="storeId" label="所属门店">
            <Select
              placeholder="选择门店（选填）"
              allowClear
              options={stores.map(s => ({ label: s.name, value: s.id }))}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序" extra="越小越靠前">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Consultants;
